import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { adminApiFetch } from "@/shared/api/adminApiFetch";
import { QUERY_CACHE_CLEAR_INTERVAL_HOURS, STATUS, TENANT_STATUS } from "@/constants";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateTenantPayload = {
  tenant_name: string;
};

export type TenantCreateResponse = {
  job_id: string;
  tenant_id: number;
};

export type CacheClearResponse = {
  scope: "tenant" | "all";
  tenant_id: number | null;
  deleted_keys: number;
};

export type UpdateQueryCacheIntervalPayload = {
  tenantId: number;
  intervalHours: number;
};

export type QueryCacheSettingsResponse = {
  tenant_id: number;
  interval_hours: number;
  next_clear_at: string;
};

export type TenantStatus = (typeof TENANT_STATUS)[keyof typeof TENANT_STATUS];

export type TenantSummary = {
  id: number;
  name: string;
  status: TenantStatus;
  created_at: string;
  query_cache_clear_interval_hours: number;
};

export type TenantsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  tenants: TenantSummary[];
};

export const tenantsInitialState: TenantsState = {
  status: STATUS.IDLE,
  error: null,
  tenants: [],
};

const tenantStatusValues = new Set<string>(Object.values(TENANT_STATUS));

function isTenantStatus(value: unknown): value is TenantStatus {
  return typeof value === "string" && tenantStatusValues.has(value);
}

function parseTenantSummaryRow(value: unknown): TenantSummary | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (
    typeof row.tenant_id !== "number" ||
    typeof row.tenant_name !== "string" ||
    !isTenantStatus(row.status) ||
    typeof row.created_at !== "string"
  ) {
    return null;
  }
  return {
    id: row.tenant_id,
    name: row.tenant_name,
    status: row.status,
    created_at: row.created_at,
    query_cache_clear_interval_hours:
      typeof row.query_cache_clear_interval_hours === "number"
        ? row.query_cache_clear_interval_hours
        : QUERY_CACHE_CLEAR_INTERVAL_HOURS.DEFAULT,
  };
}

function parseTenantsResponse(data: unknown): TenantSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  const tenants = data.map(parseTenantSummaryRow);
  if (tenants.some((tenant) => tenant === null)) {
    return null;
  }
  return tenants as TenantSummary[];
}

function isTenantCreateResponse(value: unknown): value is TenantCreateResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (typeof row.job_id === "string" || typeof row.job_id === "number") &&
    typeof row.tenant_id === "number"
  );
}

function isCacheClearResponse(value: unknown): value is CacheClearResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (row.scope === "tenant" || row.scope === "all") &&
    (row.tenant_id === null || typeof row.tenant_id === "number") &&
    typeof row.deleted_keys === "number"
  );
}

export const createTenant = createAsyncThunk<
  TenantCreateResponse,
  CreateTenantPayload,
  { rejectValue: string }
>("tenants/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantCreateResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return {
      job_id: String(data.job_id),
      tenant_id: data.tenant_id,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const clearTenantCache = createAsyncThunk<
  CacheClearResponse,
  number,
  { rejectValue: string }
>("tenants/clearCache", async (tenantId, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants/cache/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: tenantId }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCacheClearResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const clearAllTenantsCache = createAsyncThunk<
  CacheClearResponse,
  void,
  { rejectValue: string }
>("tenants/clearAllCache", async (_, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants/cache/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCacheClearResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isQueryCacheSettingsResponse(
  value: unknown,
): value is QueryCacheSettingsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.tenant_id === "number" &&
    typeof row.interval_hours === "number" &&
    typeof row.next_clear_at === "string"
  );
}

export const updateTenantQueryCacheInterval = createAsyncThunk<
  QueryCacheSettingsResponse,
  UpdateQueryCacheIntervalPayload,
  { rejectValue: string }
>(
  "tenants/updateQueryCacheInterval",
  async ({ tenantId, intervalHours }, { rejectWithValue }) => {
    try {
      const res = await adminApiFetch(`/v1/tenants/${tenantId}/query-cache`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interval_hours: intervalHours }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isQueryCacheSettingsResponse(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchTenants = createAsyncThunk<
  TenantSummary[],
  void,
  { rejectValue: string }
>("tenants/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const tenants = parseTenantsResponse(data);
    if (!tenants) {
      return rejectWithValue("Unexpected response shape");
    }
    return tenants;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const tenantsSlice = createSlice({
  name: "tenants",
  initialState: tenantsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.tenants = action.payload;
      })
      .addCase(fetchTenants.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.tenants = [];
        state.error = null;
      })
      .addCase(updateTenantQueryCacheInterval.fulfilled, (state, action) => {
        const tenant = state.tenants.find(
          (candidate) => candidate.id === action.payload.tenant_id,
        );
        if (tenant) {
          tenant.query_cache_clear_interval_hours = action.payload.interval_hours;
        }
      });
  },
});

export const tenantsReducer = tenantsSlice.reducer;
